import { ForgotPasswordPage } from '@/pages/auth/forget-password'
import { OTPVerificationPage } from '@/pages/auth/otp-verification'
import SignInPage from '@/pages/auth/sign-in'
import SignUpPage from '@/pages/auth/sign-up'
import ProtectedRoute from '@/routes/protected-route'
import { lazy } from 'react'
import { Route, Routes } from 'react-router'
import PageTitle from './components/page-title'
import { useActiveOrg } from './hooks/use-active-org.ts'
import { useFormShortcuts } from './hooks/use-form-shortcuts'
import { usePermissions } from './hooks/use-permissions'
import { useSentryUser } from './hooks/use-sentry-user.ts'
import LazyLoading from './LazyLoading'
import PageNotFound from './pages/dashboard/common/404'
import Unauthorise from './pages/dashboard/common/unauthorise'
import VendorWelcomePage from './pages/portal/vendor-welcome-page.tsx'
import ExemptionReviewAndApprovalPage from './pages/public/exemption-review-approval-page.tsx'
import MeetingMinuteReviewPage from './pages/public/meeting-minute-review-page'
import PoliciesAcknowledgementPage from './pages/public/policies-acknowledgement-page.tsx'
import PolicyReviewAndApprovalPage from './pages/public/policy-review-approval-page.tsx'
import PolicyReviewPage from './pages/public/policy-review-page'
import ServiceProviderAssessmentReviewPage from './pages/public/service-provider-assessment-page.tsx'
import SignatureVerificationPage from './pages/public/signature-verification-page.tsx'
import VendorRegistrationPage from './pages/public/vendor-registration-page.tsx'
import { SocketProvider } from './providers/socket.provider.tsx'
import { OrgRoute, SuperAdminRoute, VendorRoute } from './routes/index.route'
import DashboardTemplate from './templates/dashboard.template'
import { isAdminRole, isOrgRole, isVendorRole } from './utils/role-check.utils.ts'
const PrivacyPolicyPage = LazyLoading(lazy(async () => await import('@/pages/public/privacy-policy-page.tsx')))
const TermsOfServicePage = LazyLoading(lazy(async () => await import('@/pages/public/terms-of-service-page.tsx')))

function App() {
  const { activeOrg } = useActiveOrg()
  const { canNavigate } = usePermissions()

  // Sync the signed-in Clerk user with Sentry so events are tied to an account.
  useSentryUser()

  // Cmd/Ctrl+Enter submits and Esc cancels whichever form has focus.
  useFormShortcuts()

  return (
    <PageTitle>
      <Routes>
        {/* AUTH ROUTES */}
        <Route path='/sign-in' element={<SignInPage />} />
        <Route path='/sign-up' element={<SignUpPage />} />
        <Route path='/verify-otp' element={<OTPVerificationPage />} />
        <Route path='/forget-password' element={<ForgotPasswordPage />} />
        <Route path='/404' element={<PageNotFound />} />

        <Route path='/meeting-minutes/review-request/:token' element={<MeetingMinuteReviewPage />} />
        <Route path='/policy/secure/:token' element={<PolicyReviewPage />} />
        <Route path='/policy/secure-bundle/:token' element={<PoliciesAcknowledgementPage />} />
        <Route path='/policy/review-request/:token' element={<PolicyReviewAndApprovalPage type='review' />} />
        <Route path='/policy/approval-request/:token' element={<PolicyReviewAndApprovalPage type='approval' />} />
        <Route
          path='/exemptions-register/review-request/:token'
          element={<ExemptionReviewAndApprovalPage type='review' />}
        />
        <Route
          path='/exemptions-register/approval-request/:token'
          element={<ExemptionReviewAndApprovalPage type='approval' />}
        />
        <Route path='/service-provider-assessment/secure/:token' element={<ServiceProviderAssessmentReviewPage />} />
        {/* Reached from the QR code on a Certificate of Signature - must stay public. */}
        <Route path='/verify-signature/:id' element={<SignatureVerificationPage />} />
        <Route path='/portal/register' element={<VendorRegistrationPage />} />
        <Route path='/portal/welcome' element={<VendorWelcomePage />} />
        <Route path='/terms-of-service' element={<TermsOfServicePage />} />
        <Route path='/privacy-policy' element={<PrivacyPolicyPage />} />

        {/* DASHBOARD ROUTES - PROTECTED */}
        <Route
          path='/'
          element={
            <ProtectedRoute>
              <SocketProvider>
                <DashboardTemplate />
              </SocketProvider>
            </ProtectedRoute>
          }
        >
          {/* VENDOR (supply chain portal) ROUTES */}
          {VendorRoute &&
            VendorRoute?.length > 0 &&
            activeOrg?.role &&
            isVendorRole(activeOrg?.role as string) &&
            VendorRoute?.map((route, index) => (
              <Route path={route?.path} element={route?.element} key={`vendor-${index}`}>
                {route?.children &&
                  route?.children?.length > 0 &&
                  route?.children.map((child, subIdx) => (
                    <Route path={child?.path} element={child?.element} key={`vendor-${subIdx}`} />
                  ))}
              </Route>
            ))}

          {OrgRoute &&
            OrgRoute?.length > 0 &&
            OrgRoute?.map((route, index) => {
              const isAllowed = canNavigate(route?.role, route?.feature)
              // Vendors are not org-staff - their routes are rendered above.
              if (isVendorRole(activeOrg?.role as string)) return null
              if (!isOrgRole(activeOrg?.role as string)) return <Route path='*' element={<Unauthorise />} key={index} />
              if (!isAllowed) return <Route path='*' element={<Unauthorise />} key={index} />

              return (
                <Route path={route?.path} element={route?.element} key={index}>
                  {route?.children &&
                    route?.children?.length > 0 &&
                    route?.children.map((child, subIdx) => {
                      const isAllowed = canNavigate(child?.role, child?.feature)
                      if (!isAllowed) return <Route path='*' element={<Unauthorise />} key={subIdx} />

                      return <Route path={child?.path} element={child?.element} key={subIdx} />
                    })}
                </Route>
              )
            })}

          {SuperAdminRoute &&
            SuperAdminRoute?.length > 0 &&
            SuperAdminRoute?.map((route, index) => {
              const isAllowed = canNavigate(route?.role, route?.feature)
              if (!isAdminRole(activeOrg?.role as string))
                return <Route path='*' element={<Unauthorise />} key={index} />
              if (!isAllowed) return <Route path='*' element={<Unauthorise />} key={index} />

              return (
                <Route path={route?.path} element={route?.element} key={index}>
                  {route?.children &&
                    route?.children?.length > 0 &&
                    route?.children.map((child, subIdx) => {
                      const isAllowed = canNavigate(child?.role, child?.feature)
                      if (!isAllowed) return <Route path='*' element={<Unauthorise />} key={subIdx} />

                      return <Route path={child?.path} element={child?.element} key={subIdx} />
                    })}
                </Route>
              )
            })}
        </Route>
      </Routes>
    </PageTitle>
  )
}

export default App
